import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // Don't send demo_token to backend - it's not a real JWT
    if (token && token !== 'demo_token') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
let isRedirecting = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Prevent redirect loop - only redirect once and not if already on login page
      const isOnLoginPage = window.location.pathname === '/login';
      const token = localStorage.getItem('token');

      if (!isRedirecting && !isOnLoginPage && token && token !== 'demo_token') {
        isRedirecting = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('architect-simulator-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  googleAuth: (credential) => api.post('/auth/google', { credential }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  getInventory: () => api.get('/user/inventory'),
  getLeaderboard: (limit = 10) => api.get(`/user/leaderboard?limit=${limit}`),
  getProgressTree: () => api.get('/user/progress-tree'),
  getAchievements: () => api.get('/user/achievements'),
  getStreak: () => api.get('/user/streak'),
  checkInStreak: () => api.post('/user/streak/checkin'),
  clearProgress: () => api.delete('/user/progress'),
  // Draft designs
  saveDraft: (challengeId, designJson) => api.post('/user/drafts', { challenge_id: challengeId, design_json: designJson }),
  getDraft: (challengeId) => api.get(`/user/drafts/${challengeId}`),
  deleteDraft: (challengeId) => api.delete(`/user/drafts/${challengeId}`),
  // AI rate limit
  getAIRateLimit: () => api.get('/user/ai-rate-limit'),
  // Claim anonymous submissions after login/signup
  claimAnonymousSubmissions: (fingerprint) => api.post('/user/claim-anonymous-submissions', { fingerprint }),
};

// Achievements API
export const achievementsAPI = {
  getAll: () => api.get('/achievements'),
  claim: (id) => api.post(`/user/achievements/${id}/claim`),
};

// Workshops API
export const workshopsAPI = {
  getAll: () => api.get('/workshops'),
  getBySlug: (slug) => api.get(`/workshops/${slug}`),
  getStep: (slug, step) => api.get(`/workshops/${slug}/step/${step}`),
  updateProgress: (slug, progress) => api.post(`/workshops/${slug}/progress`, progress),
  complete: (slug) => api.post(`/workshops/${slug}/complete`),
};

// Challenges API
export const challengesAPI = {
  getAll: () => api.get('/challenges'),
  getTree: () => api.get('/challenges/tree'),
  getById: (id) => api.get(`/challenges/${id}`),
  getLeaderboard: (id, limit = 10) => api.get(`/challenges/${id}/leaderboard?limit=${limit}`),
};

// Public API (no auth required)
export const publicAPI = {
  getChallengePreview: (id) => api.get(`/public/challenges/${id}`),
  // Lead magnet
  getLeadMagnetChallenge: () => api.get('/public/lead-magnet'),
  submitAnonymousDesign: (challengeId, data) =>
    api.post(`/public/challenges/${challengeId}/submit`, data),
  checkAnonymousAttempt: (challengeId, fingerprint) =>
    api.get(`/public/challenges/${challengeId}/attempt`, { params: { fingerprint } }),
};

// Components API
export const componentsAPI = {
  getAll: (category = null) =>
    api.get('/components', category ? { params: { category } } : {}),
  getById: (id) => api.get(`/components/${id}`),
  getInventory: () => api.get('/components/inventory'),
  unlock: (id) => api.post(`/components/${id}/unlock`),
  getCategories: () => api.get('/components/meta/categories'),
};

// Submissions API
export const submissionsAPI = {
  submit: (submission) => api.post('/submissions', submission),
  getById: (id) => api.get(`/submissions/${id}`),
  getByChallengeId: (challengeId) => api.get(`/submissions/challenge/${challengeId}`),
  getHistory: (limit = 20, offset = 0, difficulty = 'all') => {
    let url = `/submissions/user/history?limit=${limit}&offset=${offset}`;
    if (difficulty && difficulty !== 'all') {
      url += `&difficulty=${difficulty}`;
    }
    return api.get(url);
  },
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getStatus: () => api.get('/subscription/status'),
  createOrder: (planId) => api.post('/subscription/order', { plan_id: planId }),
  verifyPayment: (data) => api.post('/subscription/verify', data),
  cancelSubscription: (reason) => api.post('/subscription/cancel', { reason }),
  getHistory: () => api.get('/subscription/history'),
};

// Economy API (DP spending system)
export const economyAPI = {
  getStatus: () => api.get('/economy/status'),
  purchaseAICredits: (quantity = 1) => api.post('/economy/ai-credits/purchase', { quantity }),
  unlockSolution: (challengeId) => api.post(`/economy/solutions/${challengeId}/unlock`),
  checkSolutionAccess: (challengeId) => api.get(`/economy/solutions/${challengeId}/access`),
  getTransactions: (limit = 20, offset = 0) =>
    api.get(`/economy/transactions?limit=${limit}&offset=${offset}`),
};

// Helper functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

export default api;
